import { useEffect } from 'react';
import { useSimulationStore } from '../store/simulationStore';

/**
 * Hook to handle keyboard shortcuts for undo/redo and other operations
 * Ctrl+Z (or Cmd+Z): Undo
 * Ctrl+Y or Ctrl+Shift+Z (or Cmd+Y, Cmd+Shift+Z): Redo
 */
export const useKeyboardShortcuts = () => {
    const undo = useSimulationStore((state) => state.undo);
    const redo = useSimulationStore((state) => state.redo);
    const canUndo = useSimulationStore((state) => state.canUndo);
    const canRedo = useSimulationStore((state) => state.canRedo);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            // Ignore shortcuts when typing in input fields
            const target = e.target as HTMLElement;
            if (
                target.tagName === 'INPUT' ||
                target.tagName === 'TEXTAREA' ||
                target.isContentEditable
            ) {
                return;
            }

            const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
            const modifierKey = isMac ? e.metaKey : e.ctrlKey;

            // Undo: Ctrl+Z (or Cmd+Z on Mac)
            if (modifierKey && e.key === 'z' && !e.shiftKey) {
                if (canUndo) {
                    e.preventDefault();
                    undo();
                }
            }

            // Redo: Ctrl+Shift+Z or Ctrl+Y (or Cmd+Shift+Z, Cmd+Y on Mac)
            if (modifierKey && (e.shiftKey && e.key === 'z' || e.key === 'y')) {
                if (canRedo) {
                    e.preventDefault();
                    redo();
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [undo, redo, canUndo, canRedo]);
};

